import React from "react";
import { motion } from "framer-motion";
import { useLang } from "@/lib/i18n";
import { ArrowDown, Leaf, Droplets, Sprout, Cpu, BatteryCharging, ShieldCheck, ArrowUpRight } from "lucide-react";

// Icon map keyed by i18n card key — reorder-safe across languages.
const FUNDING_ICONS = {
  climate: Leaf,
  water: Droplets,
  green: Sprout,
  smart: Cpu,
  mobility: BatteryCharging,
  security: ShieldCheck,
};
const FUNDING_KEYS = ["climate", "water", "green", "smart", "mobility", "security"];

export default function Financing() {
  const { t, lang } = useLang();

  const requestGuide = () => {
    window.dispatchEvent(
      new CustomEvent("berlincube:prefill-contact", {
        detail: { message: t.financing.funding.guideMessage, subject: "Förderleitfaden" },
      })
    );
    document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
  };
  const flow = t.financing.flow;

  return (
    <section
      id="financing"
      data-testid="section-financing"
      className="relative bg-white text-[#1F2328] py-32 md:py-48"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-14">
        {/* Header */}
        <div className="grid lg:grid-cols-12 gap-8 mb-20 md:mb-28">
          <div className="lg:col-span-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="flex items-center gap-3 text-[11px] tracking-[0.35em] uppercase text-[#1F2328]/50 mb-8">
                <span className="h-px w-8 bg-[#B18B5E]" />
                {t.financing.kicker}
              </div>
              <h2
                className="font-display font-light tracking-[-0.03em] leading-[1]"
                style={{ fontSize: "clamp(3rem, 8vw, 7rem)" }}
              >
                {t.financing.title}
              </h2>
            </motion.div>
          </div>
          <div className="lg:col-span-4 flex items-end">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, delay: 0.15 }}
              className="text-[#1F2328]/65 text-base leading-relaxed"
            >
              {t.financing.body}
            </motion.p>
          </div>
        </div>

        {/* SPRINT 07 · WATERFALL INFOGRAPHIC */}
        <div className="relative max-w-[880px] mx-auto">
          {/* Central vertical spine — desktop only */}
          <div
            aria-hidden
            className="hidden md:block absolute left-1/2 top-6 bottom-6 w-px -translate-x-1/2 bg-gradient-to-b from-transparent via-[#B18B5E]/40 to-transparent"
          />

          {flow.map((step, i) => {
            const side = i % 2 === 0 ? "left" : "right";
            const isLast = i === flow.length - 1;
            return (
              <motion.div
                key={step.t}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.9, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`financing-step-${i}`}
                className="relative grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-16 pb-10 md:pb-16"
              >
                {/* Node dot on spine — desktop only */}
                <div className="hidden md:block absolute left-1/2 top-4 -translate-x-1/2 z-10">
                  <div className="h-3 w-3 rounded-full bg-[#B18B5E] shadow-[0_0_0_5px_rgba(177,139,94,0.15)]" />
                </div>
                {/* Step number — mobile: inline, desktop: on spine */}
                <div className="md:hidden text-[10px] tracking-[0.35em] uppercase text-[#B18B5E] font-display mb-2">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="hidden md:block absolute left-1/2 top-9 -translate-x-1/2 text-[10px] tracking-[0.35em] uppercase text-[#B18B5E] font-display">
                  {String(i + 1).padStart(2, "0")}
                </div>

                {/* Card */}
                <div
                  className={`${
                    side === "left"
                      ? "md:col-start-1 md:text-right md:pr-10"
                      : "md:col-start-2 md:pl-10"
                  } pt-0`}
                >
                  <div
                    className={`inline-block ${
                      side === "left" ? "md:text-right" : "md:text-left"
                    }`}
                  >
                    <h3
                      className="font-display font-light tracking-[-0.02em] leading-[1.05] text-[#1F2328]"
                      style={{ fontSize: "clamp(1.35rem, 2.6vw, 2.25rem)" }}
                    >
                      {step.t}
                    </h3>
                    <p className="mt-3 max-w-full md:max-w-[320px] text-[14px] leading-relaxed text-[#1F2328]/60">
                      {step.d}
                    </p>
                    <div
                      className={`mt-4 h-px w-10 bg-[#B18B5E] ${
                        side === "left" ? "md:ml-auto" : ""
                      }`}
                    />
                  </div>
                </div>

                {/* Arrow connector (below, centered) — desktop only */}
                {!isLast && (
                  <div className="hidden md:block absolute left-1/2 -bottom-1 -translate-x-1/2 z-10">
                    <ArrowDown
                      className="h-4 w-4 text-[#B18B5E]/60"
                      strokeWidth={1.5}
                    />
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, delay: 0.3 }}
          className="mt-16 text-[13px] text-[#1F2328]/55 max-w-3xl leading-relaxed italic border-l-2 border-[#B18B5E]/40 pl-5 mx-auto"
        >
          {t.financing.note}
        </motion.p>

        {/* STAGE 4 · FÖRDERLANDSCHAFTEN */}
        {t.financing.funding && (
          <div className="mt-32 md:mt-44 pt-20 md:pt-28 border-t border-[#1F2328]/10" data-testid="financing-funding">
            <div className="grid lg:grid-cols-12 gap-8 mb-14 md:mb-20">
              <div className="lg:col-span-8">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                >
                  <div className="flex items-center gap-3 text-[11px] tracking-[0.35em] uppercase text-[#1F2328]/50 mb-6 font-display">
                    <span className="h-px w-8 bg-[#B18B5E]" />
                    {t.financing.funding.kicker}
                  </div>
                  <h3
                    className="font-display font-light tracking-[-0.025em] leading-[1.05]"
                    style={{ fontSize: "clamp(2rem, 5vw, 4rem)" }}
                  >
                    {t.financing.funding.title}
                  </h3>
                </motion.div>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#1F2328]/10 border border-[#1F2328]/10">
              {t.financing.funding.cards.map((c, i) => {
                const Icon = FUNDING_ICONS[FUNDING_KEYS[i]] || Leaf;
                return (
                  <motion.div
                    key={c.t}
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{ duration: 0.8, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
                    data-testid={`funding-card-${i}`}
                    className="bg-white p-8 md:p-10 flex flex-col gap-5 min-h-[220px]"
                  >
                    <div className="flex items-center justify-between">
                      <Icon className="h-6 w-6 text-[#B18B5E]" strokeWidth={1.4} aria-hidden />
                      <span className="text-[10px] tracking-[0.35em] uppercase text-[#1F2328]/35 font-display">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                    </div>
                    <div>
                      <h4
                        className="font-display font-light tracking-[-0.015em] leading-[1.15] text-[#1F2328]"
                        style={{ fontSize: "clamp(1.05rem, 1.4vw, 1.35rem)" }}
                      >
                        {c.t}
                      </h4>
                      <p className="mt-3 text-[13.5px] leading-relaxed text-[#1F2328]/60">
                        {c.d}
                      </p>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, delay: 0.2 }}
              data-testid="funding-outro"
              className="mt-14 md:mt-16 text-[13px] md:text-sm text-[#1F2328]/60 max-w-3xl leading-relaxed italic border-l-2 border-[#B18B5E]/40 pl-5"
            >
              {t.financing.funding.outro}
            </motion.p>

            {/* Förderleitfaden — Anfrage-Strecke (keine konkreten Programme öffentlich) */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, delay: 0.1 }}
              className="mt-12 md:mt-14"
              data-testid="funding-guide-block"
            >
              <p className="text-[#1F2328]/65 font-display font-light tracking-[-0.005em] leading-[1.5] max-w-3xl text-base md:text-lg">
                {t.financing.funding.guideLine}
              </p>
              <button
                type="button"
                onClick={requestGuide}
                data-testid="funding-guide-button"
                className="group mt-6 inline-flex items-center justify-between gap-10 px-6 py-4 bg-[#F5D77A] text-[#1F2328] font-medium tracking-wide hover:bg-white transition-colors"
              >
                <span>{t.financing.funding.guideCta}</span>
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
            </motion.div>
          </div>
        )}
      </div>
    </section>
  );
}
